import type { ApolloClient, ApolloQueryResult, FetchPolicy, NormalizedCacheObject, ObservableQuery, OperationVariables, TypedDocumentNode, WatchQueryOptions } from "@apollo/client";
import type { DeepReadonly } from "../../util/type";
import type { Disposable } from "../../util/lifecycle";
import type { EventCallback, EventType } from "../../util/event-manager";
import type { ApiService } from "./api-service";
import type { GraphQLError } from "graphql";
import type { Exact } from "../../gql/graphql";
import { EventManager } from "../../util/event-manager";
import { UnhandledGraphQLError } from "../../util/error";
import { notNullish } from "../../util/nullish";


type QueryEvents<Data, Variables extends OperationVariables> = {
	loading: {
		query: Query<Data, Variables>;
	},
	data: {
		data: DeepReadonly<Data>;
		query: Query<Data, Variables>;
	},
	notFound: {
		error: GraphQLError;
		query: Query<Data, Variables>;
	},
	error: {
		error: GraphQLError;
		query: Query<Data, Variables>;
	}
};



export type QueryOptions<Data, Variables extends OperationVariables> = {
	query: TypedDocumentNode<Data, Variables>
	variables: Readonly<Variables> | undefined;
	fetchPolicy?: FetchPolicy;
};

/**
 * Represents a single GraphQL query.
 *
 * Use {@link Query.on}() to register listeners for changes to the query state.
 *
 * Use {@link Query.updateVariables}() to re-fetch the query with new variables.
 *
 * **Requires {@link Query.dispose}()ing**.
 */
export class Query<Data, Variables extends OperationVariables = Exact<{ [key: string]: never }>> implements Disposable {
	readonly #api: ApiService;
	readonly #client: ApolloClient<NormalizedCacheObject>;

	readonly #query: ObservableQuery<Data, Variables>;
	readonly #subscription: ReturnType<ObservableQuery["subscribe"]>;

	readonly #events = new EventManager<QueryEvents<Data, Variables>>();

	constructor(api: ApiService, client: ApolloClient<NormalizedCacheObject>, options: Readonly<QueryOptions<Data, Variables>>) {
		this.#api = api;
		this.#client = client;
		this.#query = this.#client.watchQuery(Query.#makeWatchQueryOptions(options));
		this.#subscription = this.#query.subscribe(this.#handleQueryResult);
	}

	static #makeWatchQueryOptions<Data, Variables extends OperationVariables>(options: Readonly<QueryOptions<Data, Variables>>): WatchQueryOptions<Variables, Data> {
		const watchQueryOptions: WatchQueryOptions<Variables, Data> = {
			query: options.query,
			notifyOnNetworkStatusChange: true,
			errorPolicy: "all",
			fetchPolicy: options.fetchPolicy,
			variables: options.variables,
		};

		return watchQueryOptions;
	}

	result(): Promise<ApolloQueryResult<Data>> {
		return this.#query.result();
	}

	readonly #handleQueryResult = async (result: ApolloQueryResult<Data>): Promise<void> => {
		if(result.loading) {
			this.#events.notify("loading", { query: this });
			return;
		}

		if(result.errors) {
			for(const error of result.errors) {
				if(!await this.#handleGraphQLError(error)) {
					throw new UnhandledGraphQLError(error);
				}
			}

			return;
		}

		if(notNullish(result.data)) {
			this.#events.notify("data", { data: result.data, query: this });
		}
	};

	async #handleGraphQLError(error: GraphQLError): Promise<boolean> {
		const result = await this.#api["handleError"](error);
		if(result.handled) {
			if(result.retry) {
				this.#query.refetch();
			}

			return true;
		}

		if(result.kind === "notFound") {
			return this.#events.notify("notFound", { error, query: this });
		}

		return this.#events.notify("error", { error, query: this });
	}

	/**
	 * Update the query's variables. If they are different to the previously
	 * fetched variables, the query will be re-fetched.
	 */
	updateVariables(variables: Readonly<Variables>): void {
		this.#query.refetch(variables);
	}

	/**
   * Register an event handler to be notified when the query state changes.
   *
	 * Available events are:
   * - **`loading`** Notified whenever the query begins loading.
   *   ```ts
   *   query.on('loading', () => console.log('loading'));
   *   ```
   *
   * - **`data`** Notified whenever the query successfully responds with new data.
   *   ```ts
   *   query.on('data', ev => console.log('data:', ev.data));
   *   ```
   *
   * - **`notFound`** Notified whenever the query results in an error with code
   *   `NOT_FOUND`. If no handler is registered for this event, an
   *   {@link UnhandledGraphQLError} will be thrown.
   *   ```ts
   *   query.on('notFound', ev => console.log('Not found!', ev.error));
   *   ```
	 */
	on<E extends EventType<QueryEvents<Data, Variables>>>(event: E, listener: EventCallback<QueryEvents<Data, Variables>, E>): this {
		this.#events.on(event, listener);
		return this;
	}

	off<E extends EventType<QueryEvents<Data, Variables>>>(event: E, listener: EventCallback<QueryEvents<Data, Variables>, E>): this {
		this.#events.off(event, listener);
		return this;
	}

	dispose(): void {
		this.#events.dispose();
		this.#subscription.unsubscribe();
	}
}
